import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'utils/common';
import moment from 'moment';
import { animateScroll } from 'react-scroll';
import NavigationButtons from 'components/NavigationButtons';
import i18n from 'utils/translate';
import { noNumbers, numbersOnly, startWithNumber } from 'utils/validators';
import Checkbox from 'components/Checkbox';
import Section from 'components/Section';
import TextField from 'components/TextField';
import RadioButton from 'components/RadioButton';
import Accordion from 'components/Accordion';
import AccordionItem from 'components/AccordionItem';
import { MAX_AGE, TRAVEL_FILTERS, GENDER_FEMALE, GENDER_MALE, countries } from 'utils/constants';
import { toast } from 'react-toastify';
import { formatPrice } from 'utils/common';
import './styles.scss';
import ErrorMessage from 'components/ErrorMessage';
import { linkTypeShape } from 'utils/types';
import FilterField from 'components-shared/FilterField';
import { updateRegionsState, filterZipsOrCommunitiesBySelection } from 'utils/regions';
import { isPersonalDataFormValid } from 'utils/validators';

const maxCoinsured = 6;

const ageCase = {
  both: {
    maxAge: MAX_AGE,
    maxAgeMeasure: 'year',
    minAge: 1,
    minAgeMeasure: 'day'
  },
  adult: {
    maxAge: MAX_AGE,
    maxAgeMeasure: 'year',
    minAge: 18,
    minAgeMeasure: 'year'
  },
  underaged: {
    maxAge: 18,
    maxAgeMeasure: 'year',
    minAge: 1,
    minAgeMeasure: 'day'
  },
  fourteen: {
    maxAge: MAX_AGE,
    maxAgeMeasure: 'year',
    minAge: 14,
    minAgeMeasure: 'year'
  }
};
const dateErrorMessages = {
  min14years: i18n('Versicherter muss mindestens das 14. Lebensjahr vollendet haben.'),
  exceeded2adults: i18n('Anzahl Erwachsene überschritten! Maximal 2 Erwachsene möglich.'),
  exceeded5underaged: i18n('Anzahl Minderjähriger überschritten!'),
  inFuture: i18n('Eingegebenes Datum muss in der Vergangenheit liegen!')
};

const propTypes = {
  backLink: PropTypes.shape(linkTypeShape),
  nextLink: PropTypes.shape(linkTypeShape).isRequired,
  data: PropTypes.shape({
    vacation: PropTypes.shape({
      type: PropTypes.oneOf([TRAVEL_FILTERS.SINGLE, TRAVEL_FILTERS.FAMILY]),
      destination: PropTypes.oneOf([TRAVEL_FILTERS.INLAND, TRAVEL_FILTERS.FOREIGN]),
      startDate: PropTypes.object,
      endDate: PropTypes.object,
      cancellation: PropTypes.bool
    }),
    personal: PropTypes.shape({
      anrede: PropTypes.number,
      vorname: PropTypes.string,
      nachname: PropTypes.string,
      geburtsdatum: PropTypes.any,
      strasse: PropTypes.string,
      nr: PropTypes.string,
      plz: PropTypes.string,
      ort: PropTypes.string,
      land: PropTypes.string,
      email: PropTypes.string,
      telefon: PropTypes.string
    }),
    coinsured: PropTypes.arrayOf(
      PropTypes.shape({
        anrede: PropTypes.number,
        vorname: PropTypes.string,
        nachname: PropTypes.string,
        geburtsdatum: PropTypes.any
      })
    ),
    paymentInfo: PropTypes.shape({
      type: PropTypes.oneOf(['hobex', 'sepa']),
      iban: PropTypes.string,
      bic: PropTypes.string
    }),
    visaRequired: PropTypes.bool
  }),
  onChange: PropTypes.func.isRequired
};
const defaultProps = {};

class PersonalData extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ...props.data,
      ...props.data.personal,
      dataLoaded: false,
      adultIndex: -1,
      minorsCounter: 0,
      paymentError: false,
      wrongDate: new Map(),
      submitted: false,
      validForm: false,
      regions: [],
      communities: [],
      allCommunities: [],
      zips: [],
      allZips: [],
      locationFirstChangedBy: undefined
    };

    this.duplicatePersonError = false;
  }

  componentDidMount() {
    animateScroll.scrollToTop({ duration: 0 });
    this.loadRegions();
  }

  loadRegions = async () => {
    const callback = () => {
      const currentSelectedZip = this.props.data?.personal.plz;
      if (currentSelectedZip) {
        this.setState({ locationFirstChangedBy: 'plz' });
        filterZipsOrCommunitiesBySelection(this, currentSelectedZip, 'plz');
      }
    };
    await updateRegionsState(this, callback);
  };

  handleZipOrCommunityChange = async (newValue, key, parentKey) => {
    const { locationFirstChangedBy, allCommunities, allZips } = this.state;
    const opposingStateKey = key === 'plz' ? 'ort' : 'plz';
    const opposingStateKeyOptions = key === 'plz' ? 'communities' : 'zips';

    // the opposing options (e.g. communities) can only be controlled via the locationFirstChangedBy option (zip)
    // or the other way around
    if (!locationFirstChangedBy) {
      this.setState({ locationFirstChangedBy: key });
    }
    this.handleDataChange(newValue, key, parentKey);

    // we reset all values to '' and list all options
    if (!newValue && locationFirstChangedBy !== opposingStateKey) {
      const options = key === 'plz' ? allCommunities : allZips;
      this.setState({ locationFirstChangedBy: undefined, [opposingStateKeyOptions]: options });
      this.handleDataChange(undefined, opposingStateKey, 'personal');
      return;
    }

    // the filtered options are already in place
    if (locationFirstChangedBy === opposingStateKey) return;

    await filterZipsOrCommunitiesBySelection(this, newValue, key);
    // we need to update personal data with opposing state (ort or plz) as well
    this.handleDataChange(this.state[opposingStateKey], opposingStateKey, parentKey);
  };

  onPaymentError = error => {
    this.setState({
      paymentError: error,
      validForm: this.refs.form.checkValidity && this.refs.form.checkValidity()
    });
  };

  updateAdultIndex = coinsured => {
    const {
      data: { vacation }
    } = this.props;

    let minorsCounter = 0;
    let minorsSum = 0;
    let maxSum = 0;
    let adult = -1;

    coinsured.forEach((person, index) => {
      maxSum += index + 1;
      if (moment.isMoment(person.geburtsdatum) && person.geburtsdatum.isValid()) {
        if (moment(person.geburtsdatum) <= moment(vacation.endDate).subtract(18, 'year')) {
          adult = index;
          this.setState({ adultIndex: adult });
        } else {
          minorsCounter++;
          minorsSum += index + 1;
        }
      }
    });

    this.setState({ minorsCounter });
    // If it's not an adult but there are already 5 underaged
    if (adult === -1 && minorsCounter === 5) {
      adult = maxSum - minorsSum - 1;
      this.setState({ adultIndex: adult });
    } else if (adult === -1) {
      this.setState({ adultIndex: -1 });
    }
  };

  handleDataChange = (newData, key, parentKey, coinsuredIndex) => {
    const { onChange, data } = this.props;
    const { submitted } = this.state;

    if (coinsuredIndex >= 0) {
      const { coinsured } = data;
      coinsured[coinsuredIndex][key] = newData;
      onChange({ key: parentKey, data: coinsured });
      if (key === 'geburtsdatum') {
        this.updateAdultIndex(coinsured);
      }
      return;
    }

    this.setState({ [`${key}`]: newData }, () => {
      if (parentKey) {
        onChange({
          key: parentKey,
          data: {
            ...data.personal,
            [`${key}`]: newData
          }
        });
      } else {
        onChange({ key, data: newData });
      }
      if (submitted) {
        this.setState({
          validForm: this.refs.form.checkValidity()
        });
      }
    });
  };

  handleSubmit = () => {
    const {
      data,
      nextLink: { go } = {
        go: () => {}
      },
      onChange = () => {}
    } = this.props;
    const { paymentError } = this.state;

    Object.keys(this.refs).forEach(key => {
      if (this.refs[key].onBlur) {
        this.refs[key].onBlur();
      }
    });

    this.setState({
      submitted: true,
      validForm: this.refs.form.checkValidity && this.refs.form.checkValidity()
    });

    if (
      !this.duplicatePersonError &&
      !paymentError &&
      this.refs.form.checkValidity &&
      this.refs.form.checkValidity() &&
      data.acceptEmailSend !== null
    ) {
      toast.dismiss();

      onChange(this.state);
      go();

      window.dataLayer.push({
        event: 'onlineabschluss',
        formname: 'Reiseversicherung',
        action: 'Schritt 2 von 4'
      });
    } else {
      toast.error(<ErrorMessage message={i18n('Bitte überprüfen Sie Ihre Angaben')} />);
    }
  };

  isDuplicate = (data, personalData) => {
    return (
      data.personal.nachname === personalData.nachname &&
      data.personal.vorname === personalData.vorname &&
      data.personal.anrede === personalData.anrede &&
      moment(data.personal.geburtsdatum).isSame(personalData.geburtsdatum)
    );
  };

  renderPerson = (person, coinsurance = false, coinsuredIndex = -1) => {
    const { adultIndex, minorsCounter, wrongDate } = this.state;
    const {
      data,
      data: { vacation }
    } = this.props;
    let maxA,
      maxAM,
      minA,
      minAM,
      customError = '';

    if (!coinsurance) {
      maxA = ageCase.fourteen.maxAge;
      maxAM = ageCase.fourteen.maxAgeMeasure;
      minA = ageCase.fourteen.minAge;
      minAM = ageCase.fourteen.minAgeMeasure;
    } else {
      if (adultIndex === -1 || (adultIndex === coinsuredIndex && minorsCounter < 5)) {
        maxA = ageCase.both.maxAge;
        maxAM = ageCase.both.maxAgeMeasure;
        minA = ageCase.both.minAge;
        minAM = ageCase.both.minAgeMeasure;
      } else if (adultIndex === coinsuredIndex) {
        maxA = ageCase.adult.maxAge;
        maxAM = ageCase.adult.maxAgeMeasure;
        minA = ageCase.adult.minAge;
        minAM = ageCase.adult.minAgeMeasure;
      } else {
        maxA = ageCase.underaged.maxAge;
        maxAM = ageCase.underaged.maxAgeMeasure;
        minA = ageCase.underaged.minAge;
        minAM = ageCase.underaged.minAgeMeasure;
      }
    }

    let maxDate = moment().subtract(minA, minAM);
    let minDate = moment(vacation.endDate).subtract(maxA, maxAM);
    wrongDate.forEach(dateObject => {
      const wrongDate = dateObject.wrongDate;
      if (dateObject.coinsuredIndex === coinsuredIndex && moment.isMoment(wrongDate) && wrongDate.isValid()) {
        if (wrongDate.isAfter(maxDate)) {
          if (wrongDate.isAfter(moment())) {
            customError = dateErrorMessages.inFuture;
          } else if (minorsCounter > 4) {
            customError = dateErrorMessages.exceeded5underaged;
          } else if (minA === 14) {
            customError = dateErrorMessages.min14years;
          }
        } else if (wrongDate.isBefore(minDate) && adultIndex !== -1) {
          customError = dateErrorMessages.exceeded2adults;
        }
      }
    });

    if (
      coinsuredIndex !== -1 &&
      coinsurance &&
      data.personal.name &&
      data.personal.nachname &&
      this.isDuplicate(data, person)
    ) {
      customError = i18n('Personendaten identisch mit versicherter Person');
      this.duplicatePersonError = true;
    } else {
      this.duplicatePersonError = false;
    }

    const parentKey = coinsuredIndex !== -1 ? 'coinsured' : 'personal';

    return (
      <div
        key={`person-${coinsuredIndex}`}
        className={`person-wrapper row ${coinsurance ? 'coinsurance-section' : ''}`}
      >
        <FilterField
          label={i18n('Anrede')}
          name="title"
          className="col-xs-12 col-md-3"
          value={person.anrede}
          options={[
            { value: GENDER_MALE, label: i18n('Herr') },
            { value: GENDER_FEMALE, label: i18n('Frau') }
          ]}
          onChange={data => {
            this.handleDataChange(data?.value, 'anrede', parentKey, coinsuredIndex);
          }}
          ref="title"
          isSearchable={false}
          required
        />
        <TextField
          label={i18n('Vorname')}
          name="first_name"
          className="col-xs-12 col-md-3"
          value={person.vorname}
          onChange={data => this.handleDataChange(data, 'vorname', parentKey, coinsuredIndex)}
          required
          pattern={noNumbers}
          ref={`name${coinsuredIndex}`}
          key={`name_${person.nachname}_${person.geburtsdatum}_${coinsuredIndex}`}
          maxLength={31}
        />
        <TextField
          label={i18n('Nachname')}
          name="last_name"
          className="col-xs-12 col-md-3"
          value={person.nachname}
          onChange={data => this.handleDataChange(data, 'nachname', parentKey, coinsuredIndex)}
          required
          pattern={noNumbers}
          ref={`lastname${coinsuredIndex}`}
          key={`lastname_${person.name}_${person.geburtsdatum}_${coinsuredIndex}`}
          maxLength={31}
        />
        <TextField
          label={i18n('Geburtsdatum')}
          name="birthdate"
          value={person.geburtsdatum}
          maxDate={maxDate}
          minDate={minDate}
          className="col-xs-12 col-md-3"
          onChange={data => this.handleDataChange(data, 'geburtsdatum', parentKey, coinsuredIndex)}
          date
          onWrongDate={wrongDate => {
            this.setState(prevState => {
              const updatedMap = new Map(prevState.wrongDate);
              updatedMap.set(coinsuredIndex, { wrongDate, coinsuredIndex });
              return { wrongDate: updatedMap };
            });
          }}
          customError={customError}
          required
          ref={`date${coinsuredIndex}`}
          key={`birthday_${person.name}_${person.nachname}_${coinsuredIndex}`}
        />
        <div className="col-md-1 col-xs-12">
          {coinsuredIndex >= 1 && (
            <span
              onClick={() => {
                this.deletePerson(coinsuredIndex);
              }}
              className="remove-person"
            >
              <i className="remove-person-icon" />
              <span>{i18n('Diese Person löschen')}</span>
            </span>
          )}
        </div>
      </div>
    );
  };

  addPerson = () => {
    const { onChange, data } = this.props;
    let newData = [...data.coinsured, { anrede: 0, vorname: null, nachname: null, geburtsdatum: null }];

    onChange({ key: 'coinsured', data: newData });
    this.updateAdultIndex(newData);
  };

  deletePerson = deletionIndex => {
    const { onChange, data } = this.props;
    const { wrongDate } = this.state;
    let coinsured = [...data.coinsured];

    coinsured.splice(deletionIndex, 1);

    // We remove the deleted person from wrongDate state
    const filteredWrongDates = new Map(
      Array.from(wrongDate).filter(([_, value]) => value.coinsuredIndex !== deletionIndex)
    );

    // We update the coinsured index of the wrongDates
    const updatedWrongDates = new Map(
      Array.from(filteredWrongDates).map(([key, value]) => {
        const newIndex = key > deletionIndex ? key - 1 : key;
        return [newIndex, { coinsuredIndex: newIndex, wrongDate: value.wrongDate }];
      })
    );

    // WrongDates after the deleted index get deleted
    // since birthday value is null
    coinsured.forEach((person, coinsuredIndex) => {
      if (!person.geburtsdatum && updatedWrongDates.get(coinsuredIndex)?.wrongDate) {
        if (coinsuredIndex >= deletionIndex) {
          updatedWrongDates.set(coinsuredIndex, { wrongDate: null, coinsuredIndex });
        }
      }
    });

    this.setState({ wrongDate: updatedWrongDates }, () => {
      onChange({ key: 'coinsured', data: coinsured });
      this.updateAdultIndex(coinsured);
    });
  };

  render() {
    const {
      data: { vacation, personal, coinsured, visaRequired, acceptEmailSend },
      data,
      renderSummaryBox,
      backLink,
      quote: { price, currency }
    } = this.props;
    const { strasse, nr, plz, ort, land, email, telefon, submitted, zips, communities, dataLoaded } = this.state;

    const emailAcceptClass = submitted && data.acceptEmailSend == null ? 'accept-error' : '';

    return (
      <div className="page-step-wrapper">
        <div className="row">
          <div className="page-step-wrapper col-xs-12 col-md-8">
            <form ref="form" className={`${submitted ? 'submitted' : ''}`} id="insurance-form">
              <Accordion>
                <AccordionItem
                  header={i18n('Versicherte Person')}
                  id="person"
                  className="highlighted"
                  defaultOpen={true}
                >
                  {this.renderPerson(personal)}
                </AccordionItem>
                <AccordionItem
                  header={i18n('Wohnadresse der versicherten Person')}
                  id="address"
                  className="highlighted"
                  questionMark={i18n('Die versicherte Person muss ihren ordentlichen Wohnsitz in Österreich haben.')}
                  defaultOpen={true}
                >
                  <div className="row">
                    <TextField
                      label={i18n('Straße')}
                      name="street"
                      className="col-xs-12 col-lg-8 col-xl-4"
                      required
                      value={strasse}
                      onChange={data => this.handleDataChange(data, 'strasse', 'personal')}
                      ref="street"
                      maxLength={27}
                    />
                    <TextField
                      label={i18n('Nr.')}
                      className="col-xs-6 col-lg-4 col-xl-2"
                      name="Nummer"
                      required
                      value={nr}
                      onChange={data => this.handleDataChange(data, 'nr', 'personal')}
                      ref="nr"
                      maxLength={10}
                      pattern={startWithNumber}
                    />
                  </div>
                  <div className="row">
                    <FilterField
                      label={i18n('PLZ')}
                      name="postal_code"
                      className="col-xs-12 col-lg-4 col-xl-2"
                      value={plz}
                      options={zips}
                      onChange={data => this.handleZipOrCommunityChange(data?.value, 'plz', 'personal')}
                      isDisabled={!dataLoaded}
                      required
                      ref="plz"
                      isFormSubmitted={submitted}
                    />
                    <FilterField
                      label={i18n('Ort')}
                      name="community"
                      className="col-xs-12 col-lg-4 col-xl-2"
                      value={ort}
                      options={communities}
                      onChange={data => this.handleZipOrCommunityChange(data?.value, 'ort', 'personal')}
                      required
                      ref="community"
                      isDisabled={!dataLoaded}
                      isFormSubmitted={submitted}
                    />
                    <TextField
                      label={i18n('Land')}
                      name="country"
                      className="col-xs-12 col-lg-4 col-xl-2"
                      value={countries[land]}
                      disabled
                      questionMark={i18n('Wohnort muss in Österreich liegen!')}
                      ref="country"
                    />
                  </div>
                </AccordionItem>
                <AccordionItem header={i18n('Kontakt')} id="contact" className="highlighted" defaultOpen={true}>
                  <div className="row">
                    <TextField
                      label={i18n('E-Mail')}
                      name="name"
                      type="email"
                      className="col-xs-12 col-lg-4 col-xl-3"
                      value={email}
                      required
                      onChange={data => this.handleDataChange(data, 'email', 'personal')}
                      ref="mail"
                    />
                    <TextField
                      label={i18n('Telefon')}
                      name="phoneNumber"
                      value={telefon}
                      required
                      pattern={numbersOnly}
                      className="col-xs-12 col-lg-4 col-xl-3"
                      onChange={data => this.handleDataChange(data, 'telefon', 'personal')}
                      ref="phone"
                      maxLength={20}
                    />
                  </div>
                </AccordionItem>
                {vacation.type === TRAVEL_FILTERS.FAMILY && (
                  <AccordionItem
                    header={i18n('Mitversicherte Person/en')}
                    id="coinsured"
                    className="highlighted"
                    questionMark={i18n(
                      'Die mitversicherte/n Person/en muss/müssen ihren ordentlichen Wohnsitz in Österreich haben.'
                    )}
                    defaultOpen={true}
                  >
                    {coinsured.map((person, index) => this.renderPerson(person, true, index))}
                    {vacation.type === TRAVEL_FILTERS.FAMILY && coinsured.length < maxCoinsured && (
                      <div className="add-person col-xs-12" onClick={this.addPerson}>
                        <i className="add-person-icon" /> {i18n('Weitere Personen hinzufügen')}
                      </div>
                    )}
                  </AccordionItem>
                )}
                <AccordionItem
                  header={i18n('Versicherungsbestätigung und Polizzenversand')}
                  id="conditions"
                  className="highlighted"
                  defaultOpen={true}
                >
                  <Section header={i18n('Versicherungsbestätigung')} className="pb-1">
                    <div className="padding-bottom col-xs-12">
                      <Checkbox
                        className="inline-block"
                        id="visaRequiredCheckbox"
                        name="visaRequired"
                        checked={visaRequired}
                        label={i18n('Versicherungsbestätigung auf Englisch (z.B. für Visumsantrag) wird benötigt')}
                        onChange={() => this.handleDataChange(!visaRequired, 'visaRequired')}
                      />
                    </div>
                  </Section>
                  <Section header={i18n('Polizzenversand')}>
                    <div className="col-xs-12">
                      <p>
                        {i18n(
                          `Nach Vertragsabschluss (durch Klick auf den Button „Online abschließen“) steht Ihre Polizze samt den gewünschten Visa-Bestätigungen zum Download bereit.`
                        )}
                      </p>
                    </div>
                    <div className="col-xs-12">
                      <RadioButton
                        className={`break-word ${emailAcceptClass}`}
                        id="acceptDocumentSend"
                        textHTML={i18n(
                          'Ich wünsche zusätzlich <strong>die Zusendung</strong> dieser Dokumente an die von mir angeführte E-Mail-Adresse. Durch die Angabe meiner E-Mail-Adresse bestätige ich, über regelmäßigen Internetzugang zu verfügen. Die hier gegebene Einwilligung kann jederzeit widerrufen werden.'
                        )}
                        checked={acceptEmailSend === true}
                        onChange={() => this.handleDataChange(true, 'acceptEmailSend')}
                      />
                    </div>
                    <div className="col-xs-12">
                      <RadioButton
                        className={`break-word ${emailAcceptClass}`}
                        id="acceptDocumentNotSend"
                        textHTML={i18n(
                          'Ich wünsche <strong>keine Zusendung</strong> dieser Dokumente an die von mir angeführte E-Mail-Adresse. Der Download direkt nach Vertragsabschluss sowie ein Bestätigungs-E-Mail über den Abschluss des Versicherungsvertrages reichen mir aus.'
                        )}
                        checked={acceptEmailSend === false}
                        onChange={() => this.handleDataChange(false, 'acceptEmailSend')}
                      />
                    </div>
                    <div className="col-xs-12">
                      <p className="email-send-description">
                        {i18n(
                          `Sie können die Polizze samt Bedingungen und gewünschten Visa-Bestätigungen zusätzlich auch in Papierform erhalten. Bitte senden Sie dazu ein E-Mail mit Ihrer Postanschrift an `
                        )}
                        <a href="mailto:reise.center.vs@grawe.at">reise.center.vs@grawe.at</a>.
                      </p>
                    </div>
                  </Section>
                </AccordionItem>
              </Accordion>
            </form>
          </div>
          <div className="page-step-wrapper col-md-4 summary-wrapper">{renderSummaryBox}</div>
        </div>
        <div className="sticky-footer">
          <div className="page-step-wrapper__price d-md-none">
            {i18n('Prämie (einmalig)')}&nbsp;
            {formatPrice(price, currency)}
          </div>
          <NavigationButtons
            backLink={backLink}
            nextLink={{
              go: () => {
                this.setState({
                  submitted: true,
                  validForm: isPersonalDataFormValid(this)
                });

                Object.keys(this.refs).forEach(key => {
                  if (this.refs[key].onBlur) {
                    this.refs[key].onBlur();
                  }
                });

                if (isPersonalDataFormValid(this) && acceptEmailSend !== null) {
                  toast.dismiss();
                  this.handleSubmit();
                } else {
                  toast.error(<ErrorMessage message={i18n('Bitte überprüfen Sie Ihre Angaben')} />);
                }
              }
            }}
          />
        </div>
      </div>
    );
  }
}
PersonalData.propTypes = propTypes;
PersonalData.defaultProps = defaultProps;

export default withRouter(PersonalData);
